import React, { useEffect, useState } from "react";
import { useAppNavigation } from "../../hooks/useAppNavigation";
import SubPages from "./SubPagesEnum";
import { FMEPageHeader, useModelContext } from '../../components/Layout';
import OMESidebar, { OMEView } from '../../components/Layout/OMESidebar';
import OperationalSplitView from "../../components/OperationalModel/OperationalSplitView";
import OperationalValuationPage from "../../components/OperationalModel/OperationalValuationPage";
import OperationalToolsPage from "../../components/OperationalModel/OperationalToolsPage";
import useOperationalData from "../../components/OperationalModel/useOperationalData";
import OperationalScenarioSelector from "../../components/OperationalModel/OperationalScenarioSelector";

// Legacy exports for backward compatibility (used by DraggableDataGroupOperational)
let STATE_REF: any = null;
let RELOAD_STATE_REF: any = null;
let RECREATE_EXCEL_INPUT_REF: any = null;
let RELOAD_STATE_FROM_API: any = null;
let RELOAD_OUTPUT_STATE_FROM_API: any = null;
let RELOAD_Sensitivity: any = null;

export default function OperationalModelEditor() {
    const { model, currentScenario, currentSensitivity } = useModelContext();

    if (!model || !currentScenario) {
        return <div className="text-center mt-6">No model data available</div>;
    }

    return (
        <OperationalModelEditorContent
            model={model}
            currentScenario={currentScenario}
            currentSensitivity={currentSensitivity}
        />
    );
}

function OperationalModelEditorContent({ model, currentScenario, currentSensitivity }) {
    const navigateToPage = useAppNavigation();
    const data = useOperationalData(model, currentScenario);
    const [currentView, setCurrentView] = useState<OMEView>('edit');

    // Screen size check
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Update legacy refs for backward compatibility
    RELOAD_STATE_REF = data.reloadState;
    RELOAD_STATE_FROM_API = data.reloadFromApi;

    // Screen too small
    if (screenWidth <= 590) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-white px-8 py-16">
                <span className="material-symbols-outlined text-slate-300 text-[48px] mb-4">aspect_ratio</span>
                <h2 className="text-xl text-center text-slate-900 mb-2">
                    Operational Model Editor / {model.name}
                </h2>
                <p className="text-base text-center text-slate-500">
                    The screen size is too small for the model editor. Please resize your window.
                </p>
            </div>
        );
    }

    // Condition computed guard
    if (data.conditionComputed) {
        return (
            <div className="flex flex-col h-full bg-white">
                <FMEPageHeader
                    title="Operational Model Editor"
                    breadcrumb={[model.name]}
                    actions={<OperationalScenarioSelector />}
                />
                <div className="flex-1 flex items-center justify-center">
                    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 max-w-md text-center">
                        <span className="material-symbols-outlined text-amber-500 text-[48px] mb-4">warning</span>
                        <h2 className="text-xl font-bold text-slate-900 mb-3">
                            Operation not allowed
                        </h2>
                        <p className="text-sm text-slate-600 mb-2">
                            You need to compute the scenario in the financial model editor first.
                        </p>
                        <p className="text-sm text-slate-500 mb-6">
                            If you already did this, wait a few seconds and retry.
                        </p>
                        <button
                            onClick={() => navigateToPage(SubPages.MyModels)}
                            className="flex items-center gap-2 mx-auto px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors text-sm text-slate-700"
                        >
                            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                            Go back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state
    if (data.loading) {
        return (
            <div className="flex flex-col h-full bg-white">
                <FMEPageHeader
                    title="Operational Model Editor"
                    breadcrumb={[model.name]}
                    description="Loading..."
                    actions={<OperationalScenarioSelector />}
                />
                <div className="flex-1 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-slate-500">Loading operational data...</p>
                    </div>
                </div>
            </div>
        );
    }

    const canEdit = model.modelRulesDeducted?.can_edit !== false;
    const hasWriteRight = data.permanentRightToWrite;
    const isReadOnly = !canEdit || !hasWriteRight;

    const readOnlyMessage = !canEdit && model.modelRulesDeducted?.can_edit_reason
        ? `Read-only mode — ${model.modelRulesDeducted.can_edit_reason}`
        : !hasWriteRight
        ? 'Read-only mode — you do not have write access to this model.'
        : '';

    return (
        <div className="flex flex-1 min-h-0 bg-white">
            {/* Sidebar */}
            <OMESidebar currentView={currentView} onViewChange={setCurrentView} />

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-h-0 min-w-0">
                {isReadOnly && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm">
                        <span className="material-symbols-outlined text-[18px]">visibility</span>
                        <span>{readOnlyMessage}</span>
                    </div>
                )}
                {currentView === 'edit' && (
                    <EditView
                        model={model}
                        currentScenario={currentScenario}
                        currentSensitivity={currentSensitivity}
                        data={data}
                    />
                )}

                {currentView === 'valuation' && (
                    <OperationalValuationPage
                        model={model}
                        currentScenario={currentScenario}
                        currentSensitivity={currentSensitivity}
                        valuationState={data.valuationState}
                        computeValuation={data.computeValuation}
                        reloadState={data.reloadState}
                        userRights={data.userRights}
                    />
                )}

                {currentView === 'tools' && (
                    <OperationalToolsPage
                        model={model}
                        currentScenario={currentScenario}
                        currentSensitivity={currentSensitivity}
                        operationalParameters={data.state.OperationalParameters}
                    />
                )}
            </div>
        </div>
    );
}

function EditView({ model, currentScenario, currentSensitivity, data }) {
    return (
        <div className="flex flex-col h-full">
            {/* Page Header */}
            <FMEPageHeader
                title="Operational Model Editor"
                breadcrumb={[model.name]}
                actions={<OperationalScenarioSelector />}
            />

            {/* Collaboration Banner */}
            <CollaborationBanner
                userRights={data.userRights}
                permanentRightToWrite={data.permanentRightToWrite}
                userEditing={data.userEditing}
                userClaimingWrite={data.userClaimingWrite}
                handleClaimWrite={data.handleClaimWrite}
                handleGiveWrite={data.handleGiveWrite}
            />

            {/* Main Content: Split View */}
            <div className="flex-1 min-h-0 overflow-hidden">
                <OperationalSplitView data={data} />
            </div>
        </div>
    );
}

// Collaboration banner component
function CollaborationBanner({
    userRights,
    permanentRightToWrite,
    userEditing,
    userClaimingWrite,
    handleClaimWrite,
    handleGiveWrite,
}: {
    userRights: any;
    permanentRightToWrite: boolean;
    userEditing: { user_id: number; user_name: string };
    userClaimingWrite: { user_id: number; user_name: string };
    handleClaimWrite: () => void;
    handleGiveWrite: () => void;
}) {
    return (
        <div className="flex items-center gap-2 text-sm">
            {userRights.user_id !== userEditing.user_id && permanentRightToWrite && userEditing.user_id !== 0 && (
                <>
                    <span className="text-slate-600">
                        <strong>{userEditing.user_name}</strong> is currently editing
                    </span>
                    <button
                        onClick={handleClaimWrite}
                        className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-primary hover:bg-primary/5 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[14px]">edit</span>
                        Claim write
                    </button>
                </>
            )}
            {userClaimingWrite.user_id !== 0 && permanentRightToWrite && (
                <>
                    <span className="text-slate-600">
                        <strong>{userClaimingWrite.user_name}</strong> is requesting write access
                    </span>
                    <button
                        onClick={handleGiveWrite}
                        className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-emerald-600 hover:bg-emerald-50 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[14px]">check</span>
                        Grant access
                    </button>
                </>
            )}
        </div>
    );
}

export { STATE_REF, RELOAD_STATE_REF, RECREATE_EXCEL_INPUT_REF, RELOAD_STATE_FROM_API, RELOAD_OUTPUT_STATE_FROM_API, RELOAD_Sensitivity }
